import { App } from '@capacitor/app';
import { Capacitor, CapacitorHttp } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { env, buildInfo } from '@/env';
import { shouldApplyBundle } from './version-compare';

// Self-hosted OTA (Capgo) - over-the-air web-bundle updates for the native
// builds. The native shell ships a "builtin" bundle; at runtime we poll a
// version.json manifest on DigitalOcean Spaces and, when it advertises a newer
// version, download + activate the new bundle without an App Store round-trip.
//
// Web builds (the dashboard in a browser) are a no-op - there is nothing to
// side-load there. The whole service short-circuits unless running natively.

interface VersionInfo {
  version: string;
  url: string;
  buildDate: string;
}

// Flipped the instant before `CapacitorUpdater.set()` tears the webview down.
// The swap destroys this JavaScript context mid-flight, so anything React is
// holding (an in-flight chunk import, a commit in progress) can throw on the
// way out. AppErrorBoundary reads this to render "Updating…" instead of
// "Something went wrong" - the app isn't broken, it's being replaced.
let applyingUpdate = false;

/** True once an OTA swap has been triggered and the reload is imminent. */
export function isApplyingUpdate(): boolean {
  return applyingUpdate;
}

/** version.json endpoint (DO Spaces, restaurant-updates/ prefix). */
const UPDATE_URL = env.updateUrl || undefined;
/** Version baked into this native build; used to interpret the "builtin" bundle. */
const BUILTIN_VERSION = buildInfo.appVersion || '0.0.0';

export class AppUpdaterService {
  private static instance: AppUpdaterService;
  private readonly updateCheckInterval = 30 * 60 * 1000; // 30 minutes
  private readonly isNative = Capacitor.isNativePlatform();

  static getInstance(): AppUpdaterService {
    if (!AppUpdaterService.instance) {
      AppUpdaterService.instance = new AppUpdaterService();
    }
    return AppUpdaterService.instance;
  }

  /**
   * Acknowledge the running bundle, then check ONCE for a newer one. Awaited
   * BEFORE React mounts (see main.tsx).
   *
   * `downloadAndInstallUpdate` ends in `CapacitorUpdater.set()`, which destroys
   * this JavaScript context and reloads the webview immediately - so on the
   * update path this promise never resolves, by design. Running it pre-render
   * is what makes that safe: with no React tree mounted and no chunk imports in
   * flight, the swap has nothing to yank out from under it. Called after
   * `createRoot` (as it once was), the very first launch of a fresh install -
   * the one launch that always finds a newer bundle - swapped mid-render and
   * put the error boundary on screen instead of the app.
   *
   * Bounded by `timeoutMs` so a slow or unreachable R2 never holds the app
   * hostage: on timeout we render on the current bundle and
   * `startUpdatePolling` picks the update up later.
   */
  async applyUpdateBeforeRender(timeoutMs = 15_000): Promise<void> {
    if (!this.isNative || !UPDATE_URL) return;

    // Capgo auto-rolls-back to the previous bundle unless the running bundle
    // acknowledges itself within appReadyTimeout (10s) on EVERY launch - so call
    // notifyAppReady unconditionally, not only when a bundle reads back as
    // "pending" (that status is racy on a cold start, and missing it silently
    // reverts every OTA to builtin). The builtin bundle is a native no-op here.
    const check = (async () => {
      await this.notifyAppReady();
      await this.checkForUpdate();
    })();

    await Promise.race([check, new Promise<void>((resolve) => setTimeout(resolve, timeoutMs))]);
  }

  /**
   * Re-acknowledge the running bundle every time the app returns to the
   * foreground.
   *
   * With `autoUpdate: false` the plugin's `appMovedToForeground` handler ends in
   * `sendReadyToJs`, which parks a background thread on a semaphore that only
   * `notifyAppReady()` releases. We call that once, pre-render - so the FIRST
   * resume was fine and every later one waited out the full `appReadyTimeout`
   * and logged `Semaphore wait timed out after 10000ms`, which Faro reported as
   * a production error on whatever screen the user happened to resume onto.
   *
   * `notifyAppReady` only re-marks the current bundle successful, so calling it
   * per resume is idempotent - and it re-arms the rollback acknowledgement that
   * the plugin re-checks on each foreground anyway.
   *
   * Guarded on `isNative` alone, not `UPDATE_URL`: the plugin foregrounds (and
   * times out) whether or not we have a manifest to poll.
   *
   * TODO(native-release): DELETE this once a native build carrying
   * @capgo/capacitor-updater >= 8.51.1 is live on devices. Upstream fixed the
   * same bug in #840 ("skip appReady semaphore wait on foreground resume"):
   * sendReadyToJs now only waits when load()/reload() actually armed the
   * semaphore, so a resume no longer waits for a notifyAppReady that is never
   * coming. package.json is already on 8.51.15 — but that is NATIVE code, and
   * an OTA only replaces the JS. Removing this before the native build ships
   * would hand every phone JS that assumes a fix its plugin does not have yet,
   * and bring the 10s freeze back. Ship the native build first, confirm
   * adoption, THEN remove this in a JS-only change.
   *
   * Check before removing: notifyAppReady also re-arms Capgo's rollback
   * acknowledgement, which the plugin re-checks on each foreground. #840 does
   * not touch that, but confirm a resume-only path still keeps the bundle
   * marked successful.
   */
  startResumeAcknowledgement(): void {
    if (!this.isNative) return;
    void App.addListener('resume', () => {
      void this.notifyAppReady();
    });
  }

  /** Keep checking while the app runs, for bundles published mid-session. */
  startUpdatePolling(): void {
    if (!this.isNative || !UPDATE_URL) return;
    setInterval(() => {
      void this.checkForUpdate();
    }, this.updateCheckInterval);
  }

  /** Fetch the manifest and download a newer bundle if one is available. */
  async checkForUpdate(): Promise<void> {
    if (!this.isNative || !UPDATE_URL) return;

    try {
      // Native HTTP, NOT window.fetch: the manifest host is cross-origin to the
      // webview (capacitor://localhost) and the R2 bucket serves no CORS headers,
      // so a browser fetch() is blocked before we ever see the version and no
      // download can start. The native layer isn't subject to CORS.
      const response = await CapacitorHttp.get({
        url: UPDATE_URL,
        headers: { 'Cache-Control': 'no-cache' },
      });
      if (response.status !== 200) {
        throw new Error(`Failed to fetch version info: HTTP ${response.status}`);
      }

      const versionInfo: VersionInfo =
        typeof response.data === 'string' ? JSON.parse(response.data) : response.data;
      const currentVersion = await this.getCurrentVersion();

      if (this.shouldUpdate(currentVersion, versionInfo.version)) {
        await this.downloadAndInstallUpdate(versionInfo);
      }
    } catch (error) {
      // warn, not error: Faro ships console.error as an EXCEPTION (kind=exception),
      // which is what the FrontendErrors alert fires on — and a manifest fetch that
      // times out is not a fault, the 30-minute poll retries it. warn still reaches
      // Loki as kind=log, so a channel that is genuinely dead (every device stuck on
      // an old bundle, silently, because the app keeps working) is still findable.
      console.warn('[AppUpdater] Error checking for updates:', error);
    }
  }

  private async downloadAndInstallUpdate(versionInfo: VersionInfo): Promise<void> {
    try {
      const { id } = await CapacitorUpdater.download({
        url: versionInfo.url,
        version: versionInfo.version,
      });
      // `set` is TERMINAL: it activates the bundle and reloads the webview at
      // once, destroying this JavaScript context - nothing after it runs and the
      // promise never resolves (see the plugin's own docs on `set`). We want that
      // immediacy, so the safety comes from WHERE this is called from: pre-render
      // on launch (applyUpdateBeforeRender). The flag covers the other caller,
      // the mid-session poll, where a React tree IS mounted.
      applyingUpdate = true;
      await CapacitorUpdater.set({ id });
    } catch (error) {
      applyingUpdate = false;
      console.error('[AppUpdater] Error downloading/installing update:', error);
    }
  }

  async getCurrentVersion(): Promise<string> {
    try {
      const current = await CapacitorUpdater.current();
      return current.bundle.version || '0.0.0';
    } catch (error) {
      console.warn('[AppUpdater] Could not read current bundle version:', error);
      return '0.0.0';
    }
  }

  /** Delegates to the pure, unit-tested comparison in ./version-compare. */
  private shouldUpdate(currentVersion: string, latestVersion: string): boolean {
    return shouldApplyBundle(currentVersion, BUILTIN_VERSION, latestVersion);
  }

  /** Acknowledge the running bundle so Capgo keeps it instead of rolling back. */
  private async notifyAppReady(): Promise<void> {
    try {
      await CapacitorUpdater.notifyAppReady();
    } catch (error) {
      console.warn('[AppUpdater] Error acknowledging running bundle:', error);
    }
  }

  /** Force-restart into the activated bundle (e.g. from a "restart to update" CTA). */
  async restartApp(): Promise<void> {
    if (!this.isNative) return;
    try {
      await CapacitorUpdater.reload();
    } catch (error) {
      console.error('[AppUpdater] Error reloading into new bundle:', error);
    }
  }
}
